/**
 * King Living Component Wrapper Override
 *
 * This overrides the pwa-contentful Component wrapper to use king-living models.
 */

import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {componentPropType} from '@salesforce/commerce-sdk-react/components';
import {useStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {NotImplemented} from '@amblique/contentful/app/components/contentful/not-implemented';
import {CONTENTFUL_METADATA} from '@amblique/contentful/app/contentful/metadata';
// Import from king-living models to get overridden components
import * as ContentfulComponents from '@amblique/king-living/app/contentful/models';
// Import king-living hook overrides
import {useTeaserTileById} from '@amblique/king-living/app/contentful/hooks/use-teaser-tile';
import {useTeaserTileBlockById} from '@amblique/king-living/app/contentful/hooks/use-teaser-tile-block';
import {useSplitPanelDoubleImageBlockById} from '@amblique/king-living/app/contentful/hooks/use-split-panel-double-image-block';
import {inflector} from '@amblique/king-living/shared/helpers/inflector';

// King Living hook overrides (use 'full' variant by default to include image data)
const KING_LIVING_HOOKS = {
  TeaserTile: useTeaserTileById,
  TeaserTileBlock: useTeaserTileBlockById,
  SplitPanelDoubleImageBlock: useSplitPanelDoubleImageBlockById,
};

/**
 * Generic component wrapper for Contentful entries
 * - Resolves component by __typename
 * - Auto-fetches full data if incomplete (Three-Tier Fragment Strategy)
 * - Applies styling and renders appropriate component
 *
 * @param {object} props
 * @param {object} props.component - Contentful component data
 * @returns {React.ReactElement}
 */
export const Component = ({component, as, ...props}) => {
  const {__typename, sys: {id}} = component;

  const ResolvedComponent = as || ContentfulComponents[__typename];
  const metadata = CONTENTFUL_METADATA[__typename];

  // Get Chakra UI styling for this component type
  const componentStyle = useStyleConfig('shared/component', {component});

  // Check if component data is complete (has all expected fields)
  const isComplete = useMemo(() => {
    if (!metadata?.fields) return true;

    return metadata.fields.every((fieldName) => Object.hasOwn(component, fieldName));
  }, [component, metadata]);

  // Auto-fetch full data if incomplete
  // Use king-living hook override if available (these default to 'full' variant)
  const useEntryById = KING_LIVING_HOOKS[__typename] || metadata?.hook;
  const shouldFetch = !isComplete && useEntryById && Boolean(component.sys?.id);

  const {data: fullComponent, isFetching} = useEntryById?.(
    id,
    {
      queryOptions: {
        enabled: shouldFetch,
      },
    }
  ) || {data: null, isFetching: false};

  // Use enriched data if available, otherwise use provided data
  const finalComponent = fullComponent || component;

  // Recalculate completeness after fetching
  const isFinalComplete = useMemo(() => {
    if (!metadata?.fields) return true;

    return metadata.fields.every((fieldName) => Object.hasOwn(finalComponent, fieldName));
  }, [finalComponent, metadata]);

  // Prepare props for the component
  const componentProps = {
    ...componentStyle,
    ...props,

    className: `component--${inflector.tableize(__typename, '-')} component--${id}`,
    isComplete: isFinalComplete,
    isFetching,
  };

  return ResolvedComponent ? (
    <ResolvedComponent component={finalComponent} {...componentProps} />
  ) : (
    <NotImplemented component={finalComponent} {...componentProps} />
  );
};

Component.displayName = 'Component';
Component.propTypes = {
  component: componentPropType.isRequired,
  as: PropTypes.elementType,
};
